import { useState } from 'react'
import { useFEFORecommendation } from '@hooks/useBatchTracking'
import { useProducts } from '@hooks/useProcurement'

export function FEFOPage() {
  const [selectedProductId, setSelectedProductId] = useState<number | undefined>()

  const { data: productsData } = useProducts()
  const { data, isLoading, error } = useFEFORecommendation(selectedProductId)

  const products = productsData?.data?.items || []
  const recommendations = data?.data || []

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          FEFO (First Expire First Out) Planning
        </h1>
        <p className="text-gray-600 text-sm mt-1">
          Optimal batch selection for repacking based on expiry dates
        </p>
      </div>

      <div className="card">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filter by Product (Optional)
          </label>
          <select
            value={selectedProductId || ''}
            onChange={(e) =>
              setSelectedProductId(e.target.value ? Number(e.target.value) : undefined)
            }
            className="w-full md:w-64"
          >
            <option value="">All Products</option>
            {products.map((product) => (
              <option key={product.id} value={product.id}>
                {product.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          Failed to load recommendations. Please try again.
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-8 text-gray-500">Loading recommendations...</div>
      ) : recommendations.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No available batches matching criteria.
        </div>
      ) : (
        <div className="space-y-4">
          {recommendations.map((batch: any, idx: number) => (
            <div
              key={batch.id}
              className={`card border-l-4 ${
                batch.priority === 'HIGH' ? 'border-red-500' : 'border-blue-500'
              }`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-600">
                      {idx + 1}
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">{batch.batchNo}</p>
                      <p className="text-sm text-gray-600">
                        {batch.product} • {batch.availableQty.toFixed(2)} units
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                    <div className="bg-gray-50 p-2 rounded">
                      <p className="text-xs text-gray-600 uppercase">Received</p>
                      <p className="font-medium text-sm">
                        {new Date(batch.receivedDate).toLocaleDateString()}
                      </p>
                    </div>

                    <div className="bg-gray-50 p-2 rounded">
                      <p className="text-xs text-gray-600 uppercase">Expires</p>
                      <p className="font-medium text-sm">
                        {batch.expiryDate
                          ? new Date(batch.expiryDate).toLocaleDateString()
                          : 'N/A'}
                      </p>
                    </div>

                    <div className="bg-gray-50 p-2 rounded">
                      <p className="text-xs text-gray-600 uppercase">Days Left</p>
                      <p
                        className={`font-bold text-sm ${
                          batch.daysUntilExpiry !== null && batch.daysUntilExpiry <= 7
                            ? 'text-red-600'
                            : 'text-green-600'
                        }`}
                      >
                        {batch.daysUntilExpiry !== null
                          ? batch.daysUntilExpiry > 0
                            ? `${batch.daysUntilExpiry}d`
                            : 'Expired'
                          : '∞'}
                      </p>
                    </div>

                    <div className="bg-gray-50 p-2 rounded">
                      <p className="text-xs text-gray-600 uppercase">Priority</p>
                      <p
                        className={`font-bold text-sm ${
                          batch.priority === 'HIGH'
                            ? 'text-red-600'
                            : 'text-blue-600'
                        }`}
                      >
                        {batch.priority}
                      </p>
                    </div>
                  </div>
                </div>

                {batch.priority === 'HIGH' && (
                  <div className="ml-4 p-2 bg-red-50 rounded-lg">
                    <span className="text-xs font-bold text-red-600 uppercase">
                      🔴 Prioritize
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card bg-blue-50 border-l-4 border-blue-500">
        <div className="card-header">
          <h2 className="card-title text-blue-900">FEFO Strategy</h2>
        </div>

        <div className="text-sm text-blue-900 space-y-2">
          <p>
            ✓ Batches are listed in order of expiry date (First Expire First Out)
          </p>
          <p>
            ✓ HIGH priority batches should be repacked immediately to prevent waste
          </p>
          <p>
            ✓ Follow the numbered sequence to optimize inventory and minimize expiration losses
          </p>
          <p>
            ✓ Use this plan when starting new repacking jobs
          </p>
        </div>
      </div>
    </div>
  )
}
